import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  InputAdornment,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SpreetailHeader from "../layout/SpreetailHeader";
import {
  fetchFeasibilityWeights,
  saveFeasibilityWeights,
  resetFeasibilityWeights,
  DynamicClusterSettings,
} from "../../../shared/apiService";

// ---------------------------------------------------------------------------
// Strategy metadata
// ---------------------------------------------------------------------------

interface StrategyMeta {
  key: string;
  label: string;
  description: string;
}

const STRATEGIES: StrategyMeta[] = [
  {
    key: "DEFEND_MARKET_SHARE",
    label: "Defend Market Share",
    description: "Protect your position in segments where you already have strong sales",
  },
  {
    key: "ADVERTISE_MORE",
    label: "Advertise More",
    description: "Increase ad spending to grow in segments with moderate presence",
  },
  {
    key: "UPDATE_LISTING",
    label: "Update Listing",
    description: "Improve product titles, images, or descriptions to compete better",
  },
  {
    key: "CHANGE_PRICE",
    label: "Change Price",
    description: "Adjust pricing to enter adjacent price tiers with demand",
  },
  {
    key: "MARKET_GAP",
    label: "Market Gap",
    description: "Launch new products to capture segments with zero presence",
  },
];

const STEP = 0.05;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ConfigurationSettingsPage: React.FC = () => {
  const history = useHistory();
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">("success");
  const [isClusterDefault, setIsClusterDefault] = useState(true);

  // Dynamic pricing state
  const [priceVariancePct, setPriceVariancePct] = useState<number>(10);
  const [varianceGrowthFactor, setVarianceGrowthFactor] = useState<string>("");
  const [editingVariance, setEditingVariance] = useState<string>("10");
  const [varianceError, setVarianceError] = useState<string>("");
  const [growthFactorError, setGrowthFactorError] = useState<string>("");

  // Dirty tracking — compare current values against last-saved values
  const savedVarianceRef = useRef<number>(10);
  const savedGrowthFactorRef = useRef<string>("");
  const savedWeightsRef = useRef<Record<string, number>>({});

  const isDynamicDirty =
    editingVariance !== String(savedVarianceRef.current) ||
    varianceGrowthFactor !== savedGrowthFactorRef.current;

  const hasValidationErrors = varianceError !== "" || growthFactorError !== "";

  const isFeasibilityDirty =
    Object.keys(weights).length > 0 &&
    Object.keys(savedWeightsRef.current).length > 0 &&
    JSON.stringify(weights) !== JSON.stringify(savedWeightsRef.current);

  const showToast = (message: string, severity: "success" | "error") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const commitVariance = (rawValue: string) => {
    const parsed = parseFloat(rawValue);
    if (isNaN(parsed)) {
      setEditingVariance(String(priceVariancePct));
      setVarianceError("");
      return;
    }
    if (parsed < 5 || parsed > 30) {
      setVarianceError("Must be between 5% and 30%");
      return;
    }
    setVarianceError("");
    setPriceVariancePct(parsed);
    setEditingVariance(String(parsed));
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchFeasibilityWeights();

      // Feasibility weights
      setWeights(data.feasibility_weights);
      setIsDefault(data.is_default);
      savedWeightsRef.current = { ...data.feasibility_weights };

      // Dynamic cluster settings
      const cs = data.dynamic_cluster_settings;
      const v = cs?.price_variance_pct ?? 10;
      const gf = cs?.variance_growth_factor;
      const gfStr = gf != null ? String(gf) : "";
      setPriceVariancePct(v);
      setEditingVariance(String(v));
      setVarianceGrowthFactor(gfStr);
      savedVarianceRef.current = v;
      savedGrowthFactorRef.current = gfStr;
      setIsClusterDefault(data.is_cluster_default);
    } catch {
      showToast("Failed to load settings", "error");
      const defaults = {
        DEFEND_MARKET_SHARE: 0.8,
        ADVERTISE_MORE: 0.9,
        UPDATE_LISTING: 1.0,
        CHANGE_PRICE: 0.6,
        MARKET_GAP: 0.3,
      };
      setWeights(defaults);
      setIsDefault(true);
      savedWeightsRef.current = { ...defaults };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleIncrement = (key: string) => {
    setWeights((prev) => ({
      ...prev,
      [key]: Math.min(1, parseFloat((prev[key] + STEP).toFixed(2))),
    }));
  };

  const handleDecrement = (key: string) => {
    setWeights((prev) => ({
      ...prev,
      [key]: Math.max(0, parseFloat((prev[key] - STEP).toFixed(2))),
    }));
  };

  // Local editing state — lets user type freely, validates on blur/Enter
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");

  const commitEdit = (key: string, rawValue: string) => {
    setEditingKey(null);
    setEditingValue("");
    if (rawValue === "") return;
    const parsed = parseFloat(rawValue);
    if (isNaN(parsed) || parsed < 0 || parsed > 1) return;
    setWeights((prev) => ({
      ...prev,
      [key]: parseFloat(parsed.toFixed(2)),
    }));
  };

  const handleSave = async () => {
    if (!isFeasibilityDirty && !isDynamicDirty) return;

    try {
      setSaving(true);

      // Build cluster settings payload if dirty
      let clusterPayload: DynamicClusterSettings | undefined;
      let finalVariance = priceVariancePct;
      let validGf: number | null = null;

      if (isDynamicDirty) {
        const varianceNum = parseFloat(editingVariance);
        finalVariance = isNaN(varianceNum) ? priceVariancePct : varianceNum;
        setPriceVariancePct(finalVariance);
        setEditingVariance(String(finalVariance));

        let gf = varianceGrowthFactor === "" ? null : parseFloat(varianceGrowthFactor);
        if (gf != null && (isNaN(gf) || gf < 1.0 || gf > 50.0)) gf = null;
        validGf = gf;

        clusterPayload = {
          price_variance_pct: finalVariance,
          variance_growth_factor: validGf,
        };
      }

      // Only send the sections that actually changed
      const weightsToSend = isFeasibilityDirty ? weights : undefined;
      const data = await saveFeasibilityWeights(weightsToSend, clusterPayload);
      setWeights(data.feasibility_weights);
      setIsDefault(data.is_default);
      setIsClusterDefault(data.is_cluster_default);
      savedWeightsRef.current = { ...data.feasibility_weights };

      if (isDynamicDirty) {
        savedVarianceRef.current = finalVariance;
        savedGrowthFactorRef.current = varianceGrowthFactor;
      }

      const saved: string[] = [];
      if (isFeasibilityDirty) saved.push("Feasibility weights");
      if (isDynamicDirty) saved.push("Dynamic cluster settings");
      showToast(`${saved.join(" & ")} saved`, "success");
    } catch {
      showToast("Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    setConfirmOpen(false);
    try {
      setSaving(true);
      const data = await resetFeasibilityWeights();

      // Reset feasibility weights state
      setWeights(data.feasibility_weights);
      setIsDefault(data.is_default);
      savedWeightsRef.current = { ...data.feasibility_weights };

      // Reset dynamic cluster settings state
      const cs = data.dynamic_cluster_settings;
      const v = cs?.price_variance_pct ?? 10;
      const gf = cs?.variance_growth_factor;
      const gfStr = gf != null ? String(gf) : "";
      setPriceVariancePct(v);
      setEditingVariance(String(v));
      setVarianceGrowthFactor(gfStr);
      savedVarianceRef.current = v;
      savedGrowthFactorRef.current = gfStr;
      setIsClusterDefault(true);
      setVarianceError("");
      setGrowthFactorError("");

      showToast("Reverted to defaults", "success");
    } catch {
      showToast("Failed to revert to defaults", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes confirmFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .bg-gradient-custom {
            background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
          }
          .config-scroll {
            overflow-y: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .config-scroll::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>

      <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
        {/* Decorative gradient blobs */}
        <div
          className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)",
          }}
        />
        <div
          className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)",
          }}
        />

        <SpreetailHeader />

        {/* Scrollable content */}
        <div className="flex flex-col items-center p-5 flex-1 config-scroll">
          <div className="w-full max-w-md">
            {/* Header with back button */}
            <div className="text-left mb-5">
              <div className="flex items-center gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => history.push("/market-watch")}
                  aria-label="Go back to Market Watch"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    color: "#374151",
                    fontSize: "22px",
                    lineHeight: 1,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#068E75";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#374151";
                  }}
                >
                  &#8592;
                </button>
                <h1 className="text-lg font-medium leading-none text-gray-800 m-0">
                  Configuration Settings
                </h1>
              </div>

              <p className="text-[13px] text-gray-600 mb-0 mt-0">
                Customize strategy weights and clustering settings for Market Watch analysis.
              </p>
            </div>

            {/* Loading state */}
            {loading ? (
              <div className="flex justify-center py-8">
                <CircularProgress size={28} sx={{ color: "#068E75" }} />
              </div>
            ) : (
              <>
                {/* Feasibility Weights Accordion */}
                <Accordion
                  defaultExpanded={false}
                  disableGutters
                  sx={{
                    borderRadius: "12px !important",
                    border: "1px solid #E5E7EB",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    mb: 2,
                    "&:before": { display: "none" },
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />}
                    sx={{ px: 2, minHeight: 44, "& .MuiAccordionSummary-content": { my: 1 } }}
                  >
                    <div className="flex items-center justify-between w-full mr-2">
                      <span className="text-[13px] font-semibold text-gray-800">
                        Feasibility Weights
                      </span>
                      {!isDefault && (
                        <span
                          style={{
                            color: "#068E75",
                            backgroundColor: "#D1FAE5",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: 600,
                          }}
                        >
                          Custom
                        </span>
                      )}
                    </div>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                    <p className="text-[11px] text-gray-500 m-0 mb-3">
                      A weight of 1.0 means "easy to execute" and 0.0 means "impossible." These
                      weights affect which strategies appear as top recommendations.
                    </p>

                    {STRATEGIES.map((strategy, index) => (
                      <div key={strategy.key}>
                        {index > 0 && (
                          <div
                            className="my-2.5"
                            style={{
                              borderTop: "1px solid rgba(229, 231, 235, 0.6)",
                            }}
                          />
                        )}

                        {/* Row: Label on left, controls on right */}
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[12px] font-semibold text-gray-700 flex-shrink-0">
                            {strategy.label}
                          </span>
                          <div className="flex items-center gap-1">
                            <Tooltip title="Decrease">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDecrement(strategy.key)}
                                  disabled={(weights[strategy.key] ?? 0) <= 0 || saving}
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    border: "1.5px solid #D1D5DB",
                                    borderRadius: "8px",
                                    backgroundColor: "rgba(255,255,255,0.8)",
                                    "&:hover": {
                                      backgroundColor: "#F3F4F6",
                                      borderColor: "#9CA3AF",
                                    },
                                    "&.Mui-disabled": { opacity: 0.35 },
                                  }}
                                >
                                  <RemoveIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <TextField
                              value={
                                editingKey === strategy.key
                                  ? editingValue
                                  : (weights[strategy.key]?.toFixed(2) ?? "0.00")
                              }
                              onFocus={() => {
                                setEditingKey(strategy.key);
                                setEditingValue(weights[strategy.key]?.toFixed(2) ?? "0.00");
                              }}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onBlur={() => commitEdit(strategy.key, editingValue)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  commitEdit(strategy.key, editingValue);
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              disabled={saving}
                              size="small"
                              inputProps={{
                                style: {
                                  textAlign: "center",
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  padding: "5px 4px",
                                  width: "44px",
                                  color: "#1F2937",
                                },
                                "aria-label": `${strategy.label} weight`,
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  height: "32px",
                                  backgroundColor: "rgba(255,255,255,0.9)",
                                  borderRadius: "8px",
                                  "& fieldset": {
                                    borderColor: "#D1D5DB",
                                    borderWidth: "1.5px",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#9CA3AF",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#068E75",
                                  },
                                },
                              }}
                            />
                            <Tooltip title="Increase">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleIncrement(strategy.key)}
                                  disabled={(weights[strategy.key] ?? 1) >= 1 || saving}
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    border: "1.5px solid #D1D5DB",
                                    borderRadius: "8px",
                                    backgroundColor: "rgba(255,255,255,0.8)",
                                    "&:hover": {
                                      backgroundColor: "#F3F4F6",
                                      borderColor: "#9CA3AF",
                                    },
                                    "&.Mui-disabled": { opacity: 0.35 },
                                  }}
                                >
                                  <AddIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                        </div>

                        {/* Description below — subtle, secondary */}
                        <p className="text-[11px] text-gray-400 m-0 mt-1">{strategy.description}</p>
                      </div>
                    ))}
                  </AccordionDetails>
                </Accordion>

                {/* Dynamic Cluster Settings Accordion */}
                <Accordion
                  defaultExpanded={false}
                  disableGutters
                  sx={{
                    borderRadius: "12px !important",
                    border: "1px solid #E5E7EB",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    mb: 2,
                    "&:before": { display: "none" },
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />}
                    sx={{ px: 2, minHeight: 44, "& .MuiAccordionSummary-content": { my: 1 } }}
                  >
                    <div className="flex items-center justify-between w-full mr-2">
                      <span className="text-[13px] font-semibold text-gray-800">
                        Manual Cluster Settings
                      </span>
                      {!isClusterDefault && (
                        <span
                          style={{
                            color: "#068E75",
                            backgroundColor: "#D1FAE5",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "10px",
                            fontWeight: 600,
                          }}
                        >
                          Custom
                        </span>
                      )}
                    </div>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                    <p className="text-[11px] text-gray-500 m-0 mb-3">
                      Configure how price bins are constructed when using Manual Cluster mode.
                    </p>

                    {/* Price Variance */}
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <span className="text-[12px] font-semibold text-gray-700">
                          Price Variance
                        </span>
                        <p className="text-[11px] text-gray-400 m-0 mt-1">
                          Band width for price bins (5–30%)
                        </p>
                      </div>
                      <TextField
                        value={editingVariance}
                        onChange={(e) => {
                          const val = e.target.value;
                          setEditingVariance(val);
                          const num = parseFloat(val);
                          if (val !== "" && !isNaN(num) && (num < 5 || num > 30)) {
                            setVarianceError("Must be between 5% and 30%");
                          } else {
                            setVarianceError("");
                          }
                        }}
                        onBlur={() => commitVariance(editingVariance)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            commitVariance(editingVariance);
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        size="small"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <span
                                  style={{ fontSize: "12px", color: "#6B7280", fontWeight: 500 }}
                                >
                                  %
                                </span>
                              </InputAdornment>
                            ),
                          },
                          htmlInput: {
                            style: {
                              textAlign: "center",
                              fontSize: "14px",
                              fontWeight: 600,
                              padding: "5px 4px",
                              color: "#1F2937",
                            },
                            "aria-label": "Price variance percentage",
                          },
                        }}
                        sx={{
                          width: 80,
                          flexShrink: 0,
                          "& .MuiOutlinedInput-root": {
                            height: "32px",
                            backgroundColor: "rgba(255,255,255,0.9)",
                            borderRadius: "8px",
                            paddingRight: "8px",
                            "& fieldset": {
                              borderColor: "#D1D5DB",
                              borderWidth: "1.5px",
                            },
                            "&:hover fieldset": {
                              borderColor: "#9CA3AF",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#068E75",
                            },
                          },
                        }}
                      />
                    </div>
                    {varianceError && (
                      <p className="text-[11px] m-0 mt-1" style={{ color: "#DC2626" }}>
                        {varianceError}
                      </p>
                    )}

                    <div
                      className="my-2.5"
                      style={{
                        borderTop: "1px solid rgba(229, 231, 235, 0.6)",
                      }}
                    />

                    {/* Variance Growth Factor */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-[12px] font-semibold text-gray-700">
                          Growth Factor
                        </span>
                        <p className="text-[11px] text-gray-400 m-0 mt-1">
                          Scaling factor for outer bins (blank = auto)
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Tooltip title="Decrease">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const cur =
                                  varianceGrowthFactor === ""
                                    ? 1.5
                                    : parseFloat(varianceGrowthFactor);
                                if (isNaN(cur)) return;
                                const next = parseFloat((cur - 1.0).toFixed(1));
                                if (next < 1.0) {
                                  setVarianceGrowthFactor("");
                                  setGrowthFactorError("");
                                } else {
                                  setVarianceGrowthFactor(String(next));
                                  setGrowthFactorError("");
                                }
                              }}
                              disabled={varianceGrowthFactor === ""}
                              sx={{
                                width: 30,
                                height: 30,
                                border: "1.5px solid #D1D5DB",
                                borderRadius: "8px",
                                backgroundColor: "rgba(255,255,255,0.8)",
                                "&:hover": {
                                  backgroundColor: "#F3F4F6",
                                  borderColor: "#9CA3AF",
                                },
                                "&.Mui-disabled": { opacity: 0.35 },
                              }}
                            >
                              <RemoveIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <TextField
                          value={varianceGrowthFactor}
                          onChange={(e) => {
                            const val = e.target.value;
                            setVarianceGrowthFactor(val);
                            if (val === "") {
                              setGrowthFactorError("");
                            } else {
                              const num = parseFloat(val);
                              if (isNaN(num) || num < 1.0 || num > 50.0) {
                                setGrowthFactorError("Must be ≥1.0 and ≤50");
                              } else {
                                setGrowthFactorError("");
                              }
                            }
                          }}
                          placeholder="Auto"
                          size="small"
                          inputProps={{
                            style: {
                              textAlign: "center",
                              fontSize: "14px",
                              fontWeight: 600,
                              padding: "5px 4px",
                              width: "44px",
                              color: "#1F2937",
                            },
                            "aria-label": "Variance growth factor",
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "32px",
                              backgroundColor: "rgba(255,255,255,0.9)",
                              borderRadius: "8px",
                              "& fieldset": {
                                borderColor: "#D1D5DB",
                                borderWidth: "1.5px",
                              },
                              "&:hover fieldset": {
                                borderColor: "#9CA3AF",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#068E75",
                              },
                            },
                          }}
                        />
                        <Tooltip title="Increase">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const cur =
                                  varianceGrowthFactor === ""
                                    ? 0.0
                                    : parseFloat(varianceGrowthFactor);
                                if (isNaN(cur)) return;
                                const next = parseFloat((cur + 1.0).toFixed(1));
                                if (next > 50.0) return;
                                setVarianceGrowthFactor(String(next));
                                setGrowthFactorError("");
                              }}
                              disabled={
                                varianceGrowthFactor !== "" &&
                                parseFloat(varianceGrowthFactor) >= 50.0
                              }
                              sx={{
                                width: 30,
                                height: 30,
                                border: "1.5px solid #D1D5DB",
                                borderRadius: "8px",
                                backgroundColor: "rgba(255,255,255,0.8)",
                                "&:hover": {
                                  backgroundColor: "#F3F4F6",
                                  borderColor: "#9CA3AF",
                                },
                                "&.Mui-disabled": { opacity: 0.35 },
                              }}
                            >
                              <AddIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    {growthFactorError && (
                      <p className="text-[11px] m-0 mt-1" style={{ color: "#DC2626" }}>
                        {growthFactorError}
                      </p>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Action Buttons */}
                <div className="flex gap-3 mb-4">
                  <button
                    onClick={() => setConfirmOpen(true)}
                    disabled={saving || (isDefault && isClusterDefault)}
                    className="flex-1 text-[13px] font-semibold cursor-pointer"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      border: "1.5px solid #D1D5DB",
                      borderRadius: "8px",
                      color: saving || (isDefault && isClusterDefault) ? "#9CA3AF" : "#6B7280",
                      padding: "8px 16px",
                      height: "38px",
                      fontFamily: "inherit",
                      opacity: saving || (isDefault && isClusterDefault) ? 0.6 : 1,
                      cursor: saving || (isDefault && isClusterDefault) ? "not-allowed" : "pointer",
                    }}
                  >
                    Revert to Default
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={
                      saving || hasValidationErrors || (!isFeasibilityDirty && !isDynamicDirty)
                    }
                    className="flex-1 text-[13px] font-semibold cursor-pointer"
                    style={{
                      backgroundColor: "#068E75",
                      border: "none",
                      borderRadius: "8px",
                      color: "#FFFFFF",
                      padding: "8px 16px",
                      height: "38px",
                      fontFamily: "inherit",
                      opacity:
                        saving || hasValidationErrors || (!isFeasibilityDirty && !isDynamicDirty)
                          ? 0.6
                          : 1,
                      cursor:
                        saving || hasValidationErrors || (!isFeasibilityDirty && !isDynamicDirty)
                          ? "not-allowed"
                          : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) e.currentTarget.style.backgroundColor = "#057A63";
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) e.currentTarget.style.backgroundColor = "#068E75";
                    }}
                  >
                    {saving ? <CircularProgress size={16} sx={{ color: "white" }} /> : "Save"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Overlay — matches MarketWatchRefreshConfirm pattern */}
      {confirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="revert-confirm-title"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            fontFamily: "inherit",
            background: "linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%)",
            animation: "confirmFadeIn 0.2s ease-out forwards",
          }}
        >
          {/* Decorative blur circles */}
          <div
            style={{
              position: "absolute",
              right: -40,
              top: -40,
              width: 200,
              height: 200,
              filter: "blur(40px)",
              pointerEvents: "none",
              background:
                "radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: -32,
              bottom: -32,
              width: 152,
              height: 152,
              filter: "blur(40px)",
              pointerEvents: "none",
              background:
                "radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)",
            }}
          />

          <SpreetailHeader />

          {/* Centered content */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "320px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Warning icon — teal circle */}
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  backgroundColor: "rgba(6, 142, 117, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 24,
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                    fill="#068E75"
                  />
                </svg>
              </div>

              {/* Title */}
              <h2
                id="revert-confirm-title"
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1F2937",
                  textAlign: "center",
                  margin: 0,
                  marginBottom: 8,
                }}
              >
                Revert to Defaults?
              </h2>

              {/* Description */}
              <p
                style={{
                  fontSize: "13px",
                  color: "#6B7280",
                  textAlign: "center",
                  margin: 0,
                  marginBottom: 28,
                  lineHeight: 1.5,
                }}
              >
                This will reset all feasibility weights and clustering settings to their
                original values. Your custom settings will be removed.
              </p>

              {/* Buttons — stacked full-width */}
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <button
                  onClick={handleRevert}
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: "#068E75",
                    color: "#FFFFFF",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    lineHeight: 1.4,
                    fontFamily: "inherit",
                  }}
                >
                  Yes, Revert to Defaults
                </button>
                <button
                  onClick={() => setConfirmOpen(false)}
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    borderRadius: "6px",
                    border: "1px solid #D1D5DB",
                    backgroundColor: "#FFFFFF",
                    color: "#374151",
                    fontSize: "13px",
                    fontWeight: 500,
                    cursor: "pointer",
                    lineHeight: 1.4,
                    fontFamily: "inherit",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: "100%", fontSize: "12px" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ConfigurationSettingsPage;
